import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShieldCheck, AlertTriangle } from 'lucide-react';

export default function OtpVerify() {
  const [otp, setOtp] = useState(['','','','','','']);
  const [error, setError] = useState(''); const [loading, setLoading] = useState(false);
  const [conflict, setConflict] = useState(false);
  const refs = useRef([]);
  const { verifyOtp } = useAuth(); const navigate = useNavigate(); const { state } = useLocation();

  useEffect(() => { if (!state?.userId) navigate('/login'); }, [state, navigate]);

  const handle = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const n = [...otp]; n[i] = val; setOtp(n);
    if (val && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKey = (i, e) => { if (e.key === 'Backspace' && !otp[i] && i > 0) refs.current[i - 1]?.focus(); };

  const submit = async (force = false) => {
    const code = otp.join('');
    if (code.length !== 6) return setError('Enter the 6-digit OTP');
    setError(''); setLoading(true); setConflict(false);
    try {
      const result = await verifyOtp(state.userId, code, force);
      if (result?.conflict) {
        setConflict(true);
        setLoading(false);
        return;
      }
      navigate(result.must_change_password ? '/change-password' : '/');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid OTP');
      setOtp(['','','','','','']); refs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleSubmit = async e => { e.preventDefault(); await submit(false); };

  return (
    <div className="min-h-screen bg-mesh flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)', top: '10%', right: '15%' }} />
      <div className="w-full max-w-sm" style={{ animation: 'slideUp 0.5s ease-out' }}>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
            style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', boxShadow: '0 0 32px rgba(59,130,246,0.15)' }}>
            <ShieldCheck size={28} style={{ color: '#60a5fa' }} />
          </div>
          <h1 className="text-xl font-bold gradient-text">Verify OTP</h1>
          <p className="text-sm mt-1" style={{ color: '#334155' }}>Code sent to <strong style={{ color: '#64748b' }}>{state?.email}</strong></p>
        </div>

        <div className="glass-bright rounded-2xl p-7" style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
          {error && <div className="mb-4 px-4 py-3 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171' }}>{error}</div>}

          {/* Session conflict dialog */}
          {conflict && (
            <div className="mb-5 rounded-xl p-4 space-y-3" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)' }}>
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#f59e0b' }}/>
                <div>
                  <div className="text-sm font-semibold mb-1" style={{ color: '#fbbf24' }}>Already logged in</div>
                  <div className="text-xs" style={{ color: '#92400e', lineHeight: 1.5 }}>
                    Your account is currently active in another browser or window.<br/>
                    Continuing here will <strong>automatically log out that session</strong>.
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => navigate('/login')}
                  className="flex-1 py-2 rounded-xl text-xs font-medium"
                  style={{ background: 'rgba(255,255,255,0.06)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)' }}>
                  Cancel
                </button>
                <button type="button" onClick={() => submit(true)} disabled={loading}
                  className="flex-1 py-2 rounded-xl text-xs font-semibold"
                  style={{ background: 'rgba(245,158,11,0.25)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.4)' }}>
                  {loading ? 'Logging in...' : 'Continue here — log out other session'}
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="flex gap-2 justify-center mb-6">
              {otp.map((digit, i) => (
                <input key={i} ref={el => refs.current[i] = el}
                  type="text" inputMode="numeric" maxLength={1} value={digit}
                  onChange={e => handle(i, e.target.value)} onKeyDown={e => handleKey(i, e)}
                  className="w-11 h-12 text-center text-xl font-bold rounded-xl outline-none transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: digit ? '1px solid rgba(59,130,246,0.5)' : '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0', boxShadow: digit ? '0 0 8px rgba(59,130,246,0.2)' : 'none' }} />
              ))}
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-2.5">{loading ? 'Verifying...' : 'Verify OTP'}</button>
            <button type="button" onClick={() => navigate('/login')} className="w-full mt-3 py-2 text-sm transition-colors" style={{ color: '#334155' }}
              onMouseEnter={e => e.target.style.color='#64748b'} onMouseLeave={e => e.target.style.color='#334155'}>Back to Login</button>
          </form>
        </div>
      </div>
    </div>
  );
}
